import { IconButton, Stack, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { Button } from "@platform/design-system";

import type { ReactElement } from "react";

/** A dollar amount, in the `{amount}[]` shape the benefit-description grid uses. */
export interface DollarAmount {
  amount: number;
}

/** The draft (string-backed) form of a dollar amount while it is being edited. */
export interface DraftDollarAmount {
  id: string;
  amount: string;
}

export const makeDraftDollarAmount = (amount = ""): DraftDollarAmount => ({
  id: crypto.randomUUID(),
  amount,
});

export const toDraftDollarAmounts = (
  amounts: DollarAmount[],
): DraftDollarAmount[] =>
  amounts.length > 0
    ? amounts.map((a) => makeDraftDollarAmount(String(a.amount)))
    : [makeDraftDollarAmount()];

const MAX_DOLLAR_AMOUNT = 10_000_000_000;

export const isValidDollarAmount = (entry: DraftDollarAmount): boolean => {
  const value = Number(entry.amount);

  return (
    entry.amount !== "" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= MAX_DOLLAR_AMOUNT
  );
};

interface DollarAmountsFieldProps {
  entries: DraftDollarAmount[];
  showErrors: boolean;
  maxEntries: number;
  onUpdate: (index: number, value: string) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
}

/**
 * Controlled list of whole-dollar amounts with add/remove. Used by the child
 * benefit age-band editor's "multiple dollar amounts" benefit type, and by
 * Present's BenefitDescriptionCellEditor for the same type on employee/spouse
 * benefit descriptions.
 */
export const DollarAmountsField = ({
  entries,
  showErrors,
  maxEntries,
  onUpdate,
  onAdd,
  onRemove,
}: DollarAmountsFieldProps): ReactElement => (
  <>
    {entries.map((entry, index) => (
      <Stack key={entry.id} direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          type="number"
          label="Dollar Amount"
          value={entry.amount}
          onChange={(e) => onUpdate(index, e.target.value)}
          error={showErrors && !isValidDollarAmount(entry)}
          inputProps={{ min: 1, step: 1 }}
          fullWidth
        />

        <IconButton
          size="small"
          aria-label="Remove dollar amount"
          onClick={() => onRemove(index)}
          disabled={entries.length === 1}
        >
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </Stack>
    ))}

    {entries.length < maxEntries && (
      <Button
        size="sm"
        variant="tertiary"
        startIcon={<AddIcon />}
        onClick={onAdd}
        sx={{ alignSelf: "flex-start" }}
      >
        Add amount
      </Button>
    )}
  </>
);
